import { Alert, Anchor, Button, Card, Group, Loader, Stack, Text, Title } from '@mantine/core';
import type { Attachment, DocumentReference, Patient } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { formatAppointmentDate } from '../utils/appointment-formatting';

// Hides the thumbnail sidebar in Chrome/Edge (navpanes) and Firefox (pagemode) PDF viewers.
const PDF_VIEWER_PARAMS = '#navpanes=0&pagemode=none';

function getAttachmentUrl(attachment: Attachment | undefined): string | undefined {
  if (attachment?.url) {
    return attachment.url;
  }
  return attachment?.data ? `data:${attachment.contentType};base64,${attachment.data}` : undefined;
}

function getPreviewUrl(contentType: string | undefined, url: string): string | undefined {
  if (contentType === 'application/pdf') {
    return `${url}${PDF_VIEWER_PARAMS}`;
  }
  return contentType?.startsWith('image/') || contentType?.startsWith('text/') ? url : undefined;
}

function getDocumentTitle(document: DocumentReference): string {
  return document.description ?? document.type?.text ?? document.type?.coding?.[0]?.display ?? 'Document';
}

interface DocumentDetail {
  label: string;
  value: string;
}

function getDocumentDetails(document: DocumentReference): DocumentDetail[] {
  const details: { label: string; value: string | undefined }[] = [
    { label: 'Type', value: document.type?.text ?? document.type?.coding?.[0]?.display },
    { label: 'Date', value: document.date ? formatAppointmentDate(document.date) : undefined },
    { label: 'Author', value: document.author?.[0]?.display },
    { label: 'File', value: document.content?.[0]?.attachment?.title },
  ];
  return details.filter((detail): detail is DocumentDetail => !!detail.value);
}

export function DocumentPage(): JSX.Element {
  const { documentReferenceId } = useParams();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const patient = useMedplumProfile() as Patient | undefined;
  const [document, setDocument] = useState<DocumentReference>();
  const [searching, setSearching] = useState(true);

  useEffect(() => {
    let disposed = false;
    const search = async (): Promise<void> => {
      if (!patient?.id || !documentReferenceId) {
        return;
      }
      const found = await medplum.searchOne('DocumentReference', {
        _id: documentReferenceId,
        subject: `Patient/${patient.id}`,
      });
      if (!disposed && found) {
        setDocument(found);
      }
    };
    search()
      .catch(console.error)
      .finally(() => {
        if (!disposed) {
          setSearching(false);
        }
      });
    return () => {
      disposed = true;
    };
  }, [medplum, patient?.id, documentReferenceId]);

  const attachment = document?.content?.[0]?.attachment;
  const fileUrl = getAttachmentUrl(attachment);
  const previewUrl = fileUrl ? getPreviewUrl(attachment?.contentType, fileUrl) : undefined;

  const title = document ? getDocumentTitle(document) : 'Document';
  const fileName = attachment?.title ?? title;
  const details = document ? getDocumentDetails(document) : [];

  return (
    <Stack maw={1100} mx="auto" p="md">
      <Group justify="space-between">
        <Title order={2}>{title}</Title>
        <Button variant="default" onClick={() => navigate('/appointments')}>
          Back to Appointments
        </Button>
      </Group>
      {details.length > 0 && (
        <Stack gap={4}>
          {details.map(({ label, value }) => (
            <Group key={label} gap="xs">
              <Text size="sm" fw={600}>
                {label}:
              </Text>
              <Text size="sm">{value}</Text>
            </Group>
          ))}
        </Stack>
      )}
      <Card withBorder>
        {searching && (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        )}
        {!searching && !document && (
          <Alert color="yellow" title="Document unavailable">
            This document could not be found.
          </Alert>
        )}
        {!searching && document && !fileUrl && <Text c="dimmed">This document has no file attached.</Text>}
        {previewUrl && <iframe title={title} src={previewUrl} style={{ width: '100%', height: '75vh', border: 0 }} />}
        {fileUrl && (
          <Group justify="flex-end" mt="sm">
            <Anchor href={fileUrl} download={fileName} target="_blank" rel="noopener noreferrer">
              Download
            </Anchor>
          </Group>
        )}
      </Card>
    </Stack>
  );
}
